/**
 * Recovery for the legacy rentals offline queue.
 *
 * `fieldiq_offline_queue` was a localStorage array that DeliveryConfirmation and
 * ReturnConditionCheck appended to whenever a submit happened with no
 * connection. Nothing ever read it back. The only other reference in the repo
 * was tenant-cleanup.ts, which deletes it on logout AND on login and labels it
 * "legacy offline queue fallback" — so the work was dropped, and the screen
 * meanwhile said "Saved Offline · Will sync automatically when connected".
 *
 * Both writers are gone now. This module exists for the devices that still hold
 * entries written before that change: it reads them, hands them to a banner the
 * user can act on, and clears the key once they say they have.
 *
 * IT DOES NOT REPLAY. That is a deliberate limit, not an omission. Replaying a
 * `return_condition_check` means four sequential calls — close the period,
 * create the inspection, mark the dispatch complete, then one work order per
 * damaged asset — and `delivery_confirmation` means three. None of them are
 * idempotent, and the queued payload carries no clientMutationId and no record
 * of which calls already succeeded. A replay that guessed wrong would
 * double-close a rental period or duplicate a damage work order. Losing a
 * delivery silently is bad; double-billing one silently is worse.
 *
 * So the user reads what was queued and redoes it. That recovers the
 * information without risking a corrupt write.
 */

import { formatEditedAbsolute } from './last-edited';

export const LEGACY_QUEUE_KEY = 'fieldiq_offline_queue';

export interface OrphanedEntry {
  /** 'delivery_confirmation' | 'return_condition_check', or whatever was stored. */
  type: string;
  /** Human label for the screen it came from. */
  label: string;
  orderId: string | null;
  dispatchId: string | null;
  /** ISO instant recorded at queue time; null when absent or unparseable. */
  timestamp: string | null;
  /** The whole original payload, for copy-out. Never narrowed — the point is to
   *  hand back everything the device still holds. */
  raw: Record<string, any>;
}

const TYPE_LABELS: Record<string, string> = {
  delivery_confirmation: 'Delivery confirmation',
  return_condition_check: 'Return condition check',
};

/**
 * Read the legacy queue. Returns [] for absent, empty, malformed, or
 * unavailable storage — a recovery banner must never itself be the thing that
 * breaks the screen it is attached to.
 */
export function readOrphanedQueue(): OrphanedEntry[] {
  let rawText: string | null = null;
  try {
    rawText = window.localStorage.getItem(LEGACY_QUEUE_KEY);
  } catch {
    // Storage disabled or unavailable (private mode, quota, native shell).
    return [];
  }
  if (!rawText) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed
    .filter((e): e is Record<string, any> => !!e && typeof e === 'object')
    .map((e) => ({
      type: typeof e.type === 'string' ? e.type : 'unknown',
      label:
        (typeof e.type === 'string' && TYPE_LABELS[e.type]) ||
        'Unsent rentals work',
      orderId: typeof e.order_id === 'string' && e.order_id ? e.order_id : null,
      dispatchId:
        typeof e.dispatch_id === 'string' && e.dispatch_id ? e.dispatch_id : null,
      timestamp: typeof e.timestamp === 'string' && e.timestamp ? e.timestamp : null,
      raw: e,
    }));
}

/**
 * Remove the key. Called only from an explicit user action — the banner's
 * dismiss — never on mount. The whole point of this module is that the entries
 * survive long enough to be read, and clearing them on sight would repeat the
 * original defect with extra steps.
 */
export function clearOrphanedQueue(): void {
  try {
    window.localStorage.removeItem(LEGACY_QUEUE_KEY);
  } catch {
    // Nothing to do — if storage is unreadable it is also undeletable, and the
    // next login's purgeTenantData will take it.
  }
}

/**
 * When the entry was queued, in the viewer's own zone.
 *
 * `timestamp` is a genuine INSTANT (`new Date().toISOString()` at queue time),
 * so this converts rather than reading a date prefix — the calendar-day helpers
 * would answer the UTC day and put an evening entry on tomorrow. See
 * no-instant-as-calendar-day.
 *
 * Delegates to `formatEditedAbsolute`, which already renders an instant with
 * the zone abbreviation attached. That last part earns its place here: these
 * entries are being read back weeks later, possibly by someone in the office
 * rather than the hand who made them, and "7:14 PM MDT" can be reconciled with
 * a dispatch log while a bare "7:14 PM" cannot.
 */
export function formatQueuedAt(timestamp: string | null): string {
  if (!timestamp) return 'time not recorded';
  const dt = new Date(timestamp);
  if (Number.isNaN(dt.getTime())) return 'time not recorded';
  return formatEditedAbsolute(dt);
}
